import React, { useEffect, useMemo } from 'react';

import { Form, Input, Modal } from 'antd';
import styled from 'styled-components';

import { useContractContext } from 'common/Contract/ContractContext';
import { LIVING_TYPES } from 'constants/livingTypes';
import { normalizeValue } from 'utils/helpers/form';
import { getAreaUnitLabel } from 'utils/helpers/units';

import {
  convertAccommodationAreasToBaseUnitSystem,
  getApartmentTypeGroupSlug,
  getApartmentTypeSlug,
  sortAccommodationGroupByBaseRent,
} from './helpers';
import DownCaretIcon from '../../../common/Icons/DownCaretIcon';
import { ApartmentType, ApartmentTypeGroup } from '../../../types/accommodations';
import Button from '../../lib/Button';
import InputNumber from '../../lib/InputNumber';
import Select from '../../lib/Select';
import TextArea from '../../lib/TextArea';
import { FIELD_CATEGORY } from '../constants';
import { useShopDataModalContext } from '../ShopDataModalContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 30px 30px 20px 30px;
`;

const StyledModal = styled(Modal)`
  &&& {
    .ant-modal-content {
      padding: 0px;
      line-height: 1;
      border-radius: 1px;
      user-select: none;
    }
    .ant-modal-body {
      padding: 0px;
      line-height: normal;
    }
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.span`
  font-size: 20px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const Subtitle = styled.span`
  font-size: 14px;
  color: var(--text-secondary);
  > span {
    color: var(--text-primary);
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const Info = styled.span`
  display: flex;
  flex-direction: column;
  font-size: 14px;
  color: var(--text-secondary);
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Field = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Label = styled.div`
  font-family: var(--font-bold);
  font-size: 14px;
  text-align: right;
  min-width: 100px;
  color: var(--text-primary);
`;

const Footer = styled.div`
  display: flex;
  border-top: 1px solid var(--line-gray);
  padding: 20px 30px;
  gap: 10px;
  justify-content: end;
`;

const StyledInput = styled(Input)`
  display: flex;
  align-items: center;
  background-color: var(--background-gray);
  border: 1px solid var(--line-gray);
  border-radius: 0;
  vertical-align: middle;
  line-height: normal;
  font-size: 14px;
`;

interface Props {
  open: boolean;
  apartmentTypeGroup: ApartmentTypeGroup;
  showEntranceFee: boolean;
  onClose: () => void;
}
const ShopDataModalAccommodationsAddRoomTypeVariantModal = ({
  open,
  apartmentTypeGroup,
  showEntranceFee,
  onClose,
}: Props) => {
  const [modalForm] = Form.useForm();
  const { form, currentLivingType, isCommunityCcrc, unitSource, handleDataStatusChanged, setCurrentData } =
    useShopDataModalContext();
  const { selectedKey } = useContractContext();
  const groupKey = getApartmentTypeGroupSlug(apartmentTypeGroup.id);

  const onFinish = (values: any) => {
    if (!selectedKey) {
      return;
    }

    const currentLivingTypeKey = currentLivingType?.abv;
    const groupKey = getApartmentTypeGroupSlug(apartmentTypeGroup.id);
    const typeKey = getApartmentTypeSlug(values.roomType);
    const apartmentType = apartmentTypeGroup?.apartmentTypes.find((item: ApartmentType) => item.id === values.roomType);

    const payload = {
      living_type_id: currentLivingType?.id,
      living_type_abv: currentLivingType?.abv,
      unit_type: values.unitType,
      apartment_type: apartmentType?.type ?? 'Unknown',
      apartment_type_id: values.roomType,
      apartment_type_id_slug: typeKey,
      apartment_type_group_id: apartmentTypeGroup.id,
      apartment_type_group_id_slug: groupKey,
      apartment_type_group: { id: apartmentTypeGroup?.id, name: apartmentTypeGroup?.name },
      apartment_type_groups: [{ id: apartmentTypeGroup?.id, name: apartmentTypeGroup?.name }],
      base_rent: values.baseRent,
      entrance_fee: values.entranceFee,
      sq_footage: values.squareFootage,
      price_per_sq: values.baseRent / values.squareFootage,
      notes: values.notes,
    };

    handleDataStatusChanged([FIELD_CATEGORY.ACCOMMODATIONS]);

    const nextGroupFormData = sortAccommodationGroupByBaseRent({
      ...form?.getFieldValue([FIELD_CATEGORY.ACCOMMODATIONS, groupKey]),
      [typeKey]: payload,
    });
    const nextGroupData = convertAccommodationAreasToBaseUnitSystem(nextGroupFormData, unitSource);

    form?.setFieldValue([FIELD_CATEGORY.ACCOMMODATIONS, groupKey], nextGroupFormData);

    setCurrentData((prevState: any) => ({
      ...prevState,
      [FIELD_CATEGORY.ACCOMMODATIONS]: {
        ...prevState[FIELD_CATEGORY.ACCOMMODATIONS],
        [currentLivingTypeKey]: {
          ...prevState[FIELD_CATEGORY.ACCOMMODATIONS][currentLivingTypeKey],
          [selectedKey]: {
            ...prevState[FIELD_CATEGORY.ACCOMMODATIONS][currentLivingTypeKey][selectedKey],
            [groupKey]: nextGroupData,
          },
        },
      },
    }));
    onClose();
  };

  const currentLivingTypeData = form?.getFieldValue([FIELD_CATEGORY.ACCOMMODATIONS, groupKey]);
  const existingApartmentTypes = Object.keys(currentLivingTypeData ?? {});
  const areaUnitLabel = getAreaUnitLabel(unitSource).replace('sq', 'Sq');
  const variantOptions = useMemo(
    () =>
      (apartmentTypeGroup?.apartmentTypes ?? [])
        .filter((item: ApartmentType) => !existingApartmentTypes.includes(getApartmentTypeSlug(item.id)))
        .map((item: ApartmentType) => ({
          value: item.id,
          label: item.type,
        }))
        .sort((a: any, b: any) => a.value - b.value),
    [apartmentTypeGroup, existingApartmentTypes]
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    modalForm.setFieldsValue({
      roomType: variantOptions[0]?.value,
    });
  }, [modalForm, open, variantOptions]);

  const initialValues = {
    roomType: variantOptions[0]?.value,
    unitType: undefined,
    baseRent: undefined,
    entranceFee: undefined,
    squareFootage: undefined,
    notes: '',
  };

  return (
    <StyledModal open={open} footer={null} destroyOnClose onCancel={onClose}>
      <Form form={modalForm} initialValues={initialValues} onFinish={onFinish}>
        <Container>
          <Header>
            <Title data-test-id="add-room-variant-modal-title">Add Room Variant</Title>
            {apartmentTypeGroup && (
              <Subtitle>
                for <span>{apartmentTypeGroup?.name}</span>
              </Subtitle>
            )}
          </Header>
          <Content>
            <Info>{apartmentTypeGroup && <span>Enter a variant for this accommodation type.</span>}</Info>
            <Fields>
              <Field>
                <Label>Variant</Label>
                <Form.Item
                  name={'roomType'}
                  noStyle
                  rules={[{ required: true, message: 'Please select room type variant.' }]}
                >
                  <Select
                    className="rectangular"
                    style={{ width: '100%', height: 36 }}
                    options={variantOptions}
                    disabled
                    suffixIcon={<DownCaretIcon />}
                    placeholder={'Select variant'}
                    dropdownStyle={{
                      padding: 0,
                      borderRadius: 0,
                      border: '1px solid var(--line-gray)',
                      boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)',
                    }}
                    data-test-id="room-variant-drop"
                  />
                </Form.Item>
              </Field>
              <Field>
                <Label>Unit Name (optional)</Label>
                <Form.Item name={'unitType'} noStyle>
                  <StyledInput
                    style={{ width: '100%', height: 36, border: '1px solid var(--line-gray)' }}
                    variant="filled"
                    placeholder={'Enter unit name (Unit A, Unit B, etc.)'}
                    data-test-id="unit-name-input-field"
                  />
                </Form.Item>
              </Field>

              <Field>
                <Label>{currentLivingType?.id === LIVING_TYPES.SKILLED_NURSING ? 'Daily rate' : 'Monthly rate'}</Label>
                <Form.Item
                  name={'baseRent'}
                  noStyle
                  rules={[
                    { required: true, message: 'Please provide monthly rent value.' },
                    { type: 'number', min: 0 },
                  ]}
                  normalize={normalizeValue()}
                >
                  <InputNumber style={{ width: 190, height: 36 }} prefix={'$'} data-test-id="mth-rate-input" />
                </Form.Item>
              </Field>
              {isCommunityCcrc && showEntranceFee && (
                <Field>
                  <Label>Entrance fee</Label>
                  <Form.Item
                    name={'entranceFee'}
                    noStyle
                    rules={[
                      {
                        required:
                          isCommunityCcrc &&
                          showEntranceFee &&
                          currentLivingType?.id === LIVING_TYPES.INDEPENDENT_LIVING,
                        message: 'Please provide entrance fee value.',
                      },
                      { type: 'number', min: 0 },
                    ]}
                    normalize={normalizeValue()}
                  >
                    <InputNumber style={{ width: 190, height: 36 }} prefix={'$'} data-test-id="entrance-fee-input" />
                  </Form.Item>
                </Field>
              )}
              <Field>
                <Label>{areaUnitLabel}</Label>
                <Form.Item name={'squareFootage'} rules={[{ type: 'number', min: 1 }]} noStyle>
                  <InputNumber style={{ width: 190, height: 36 }} precision={0} data-test-id="sqft-input" />
                </Form.Item>
              </Field>

              <Field>
                <Label>Note (optional)</Label>
                <Form.Item name={'notes'} noStyle>
                  <TextArea style={{ fontSize: 14 }} placeholder={'Enter a note'} data-test-id="note-input" />
                </Form.Item>
              </Field>
            </Fields>
          </Content>
        </Container>
        <Footer>
          <Button
            type={'primary'}
            style={{ fontFamily: 'var(--font-bold)', height: 40, width: 120 }}
            htmlType="submit"
            data-test-id="add-btn-variant-modal"
          >
            Add
          </Button>
          <Button
            style={{ fontFamily: 'var(--font-bold)', height: 40, width: 120 }}
            onClick={onClose}
            data-test-id="cancel-btn-variant-modal"
          >
            Cancel
          </Button>
        </Footer>
      </Form>
    </StyledModal>
  );
};

export default ShopDataModalAccommodationsAddRoomTypeVariantModal;
